import { useEffect, useRef, useCallback } from 'react';

export function useScheduledThrottle(delay: number, fn?: () => void) {
	const fnRef = useRef<(() => void) | null>(fn ?? null);
	const hasPendingRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isRunningRef = useRef(false);

	const setRefetchFn = useCallback((newFn: () => void) => {
		fnRef.current = newFn;
	}, []);

	if (fn !== undefined) {
		fnRef.current = fn;
	}

	const runCycle = useCallback(
		function cycle() {
			if (!hasPendingRef.current || !fnRef.current) {
				isRunningRef.current = false;
				timerRef.current = null;
				return;
			}

			hasPendingRef.current = false;
			fnRef.current();

			timerRef.current = setTimeout(cycle, delay);
		},
		[delay]
	);

	const schedule = useCallback(() => {
		hasPendingRef.current = true;

		if (!isRunningRef.current) {
			isRunningRef.current = true;
			timerRef.current = setTimeout(runCycle, delay);
		}
	}, [runCycle, delay]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
			isRunningRef.current = false;
			hasPendingRef.current = false;
		};
	}, []);

	return { schedule, setRefetchFn };
}
